"use client";

import { motion, useScroll, useSpring } from "framer-motion";
import { useState, useEffect } from "react";

interface ScrollProgressIndicatorProps {
    /**
     * Sayfadaki bölümlerin yukarıdan aşağıya **aynı sıra**da listesi (ör. ProductTourDetail `contentOrderSections` / `scrollSections`).
     * DOM’daki `id` sırasıyla uyuşmazsa aktif nokta yanlış bölüme geçer.
     */
    sections?: Array<{ id: string; label: string }>;
}

export default function ScrollProgressIndicator({ 
    sections = []
}: ScrollProgressIndicatorProps) {
    // Use provided sections (dinamik olarak menuItems'dan geliyor)
    const finalSections = sections;
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    const [activeSection, setActiveSection] = useState<string | null>(null);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + 200;
            
            for (let i = finalSections.length - 1; i >= 0; i--) {
                const section = document.getElementById(finalSections[i].id);
                if (section) {
                    const rect = section.getBoundingClientRect();
                    if (rect.top <= 300) {
                        setActiveSection(finalSections[i].id);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, [finalSections]);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
    };

    return (
        <div className="fixed right-8 top-1/2 -translate-y-1/2 z-40 hidden xl:block">
            {/* Progress Bar */}
            <div className="relative w-1 h-64 bg-gray-200 rounded-full mb-4">
                <motion.div
                    className="absolute top-0 left-0 w-full bg-alsero-first rounded-full origin-top"
                    style={{ scaleY: scaleX }}
                />
            </div>

            {/* Section Dots */}
            <div className="flex flex-col gap-4">
                {finalSections.map((section) => {
                    const isActive = activeSection === section.id;
                    return (
                        <motion.button
                            type="button"
                            key={section.id}
                            onClick={() => scrollToSection(section.id)}
                            className="group relative"
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <motion.div
                                className={`w-3 h-3 rounded-full transition-colors ${
                                    isActive ? 'bg-alsero-first' : 'bg-gray-400'
                                }`}
                                animate={{
                                    scale: isActive ? 1.5 : 1,
                                }}
                            />
                            <span className="absolute right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-alsero-dark-gray text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                {section.label}
                            </span>
                        </motion.button>
                    );
                })}
            </div>
        </div>
    );
}
